import type { FC, KeyboardEvent as ReactKeyboardEvent } from 'react'
import type { SearchResult, SearchResults } from '@/lib/search'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { BookOpen, GraduationCap, Headphones, Loader2, PlayCircle, Search, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { fill, localePath } from '@/i18n/config'
import { useLocale, useT } from '@/i18n'
import { lockScroll } from '@/components/generals/smooth-scroll'

interface SearchDialogProps {
    open: boolean
    onClose: () => void
}

type GroupKey = keyof SearchResults

const GROUPS: { key: GroupKey, icon: typeof BookOpen }[] = [
    { key: 'books', icon: BookOpen },
    { key: 'audiobooks', icon: Headphones },
    { key: 'videos', icon: PlayCircle },
    { key: 'courses', icon: GraduationCap }
]

const EMPTY: SearchResults = { books: [], audiobooks: [], videos: [], courses: [] }

/** Full-screen search across books, audiobooks, video lessons and courses. */
export const SearchDialog: FC<SearchDialogProps> = ({ open, onClose }) => {
    const t = useT()
    const s = t.search
    const locale = useLocale()
    const router = useRouter()
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)
    const [query, setQuery] = useState('')
    const [results, setResults] = useState<SearchResults>(EMPTY)
    const [loading, setLoading] = useState(false)
    const [failed, setFailed] = useState(false)
    const [activeIndex, setActiveIndex] = useState(0)

    useEffect(() => {
        lockScroll(open)
        if (open) window.setTimeout(() => inputRef.current?.focus(), 30)
        return () => lockScroll(false)
    }, [open])

    useEffect(() => {
        if (!open) return
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [open, onClose])

    // Close when a result opens a new page.
    useEffect(() => {
        router.events.on('routeChangeStart', onClose)
        router.events.on('hashChangeStart', onClose)
        return () => {
            router.events.off('routeChangeStart', onClose)
            router.events.off('hashChangeStart', onClose)
        }
    }, [router.events, onClose])

    // Debounced search; each new keystroke cancels the previous request.
    useEffect(() => {
        const term = query.trim()
        if (term.length < 2) {
            setResults(EMPTY)
            setLoading(false)
            setFailed(false)
            return
        }

        const controller = new AbortController()
        setLoading(true)
        const timer = window.setTimeout(async () => {
            try {
                const response = await fetch(`/api/search?q=${encodeURIComponent(term)}&locale=${locale}`, { signal: controller.signal })
                if (!response.ok) throw new Error(String(response.status))
                setResults((await response.json()) as SearchResults)
                setFailed(false)
                setActiveIndex(0)
            } catch (error) {
                if ((error as Error).name !== 'AbortError') setFailed(true)
            } finally {
                if (!controller.signal.aborted) setLoading(false)
            }
        }, 220)

        return () => {
            controller.abort()
            window.clearTimeout(timer)
        }
    }, [query, locale])

    const flat = useMemo(() => GROUPS.flatMap((group) => results[group.key]), [results])
    const total = flat.length
    const term = query.trim()

    const go = (result: SearchResult) => {
        onClose()
        void router.push(result.href)
    }

    const onInputKey = (event: ReactKeyboardEvent<HTMLInputElement>) => {
        if (!total) return
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            setActiveIndex((index) => (index + 1) % total)
        } else if (event.key === 'ArrowUp') {
            event.preventDefault()
            setActiveIndex((index) => (index - 1 + total) % total)
        } else if (event.key === 'Enter') {
            event.preventDefault()
            go(flat[activeIndex] ?? flat[0])
        }
    }

    // Once the visitor stops typing, note the search (and whether it found anything)
    // so the admin can see what people look for, especially what they do not find.
    const loggedTerm = useRef('')
    useEffect(() => {
        if (!open || loading || failed || term.length < 3 || loggedTerm.current === term.toLowerCase()) return
        const timer = window.setTimeout(() => {
            loggedTerm.current = term.toLowerCase()
            const body = JSON.stringify({ q: term, locale, results: total })
            try {
                if (!navigator.sendBeacon?.('/api/search-log', new Blob([body], { type: 'application/json' }))) {
                    void fetch('/api/search-log', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true })
                }
            } catch {
                // Logging is optional.
            }
        }, 1800)
        return () => window.clearTimeout(timer)
    }, [open, loading, failed, term, total, locale])

    useEffect(() => {
        listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)?.scrollIntoView({ block: 'nearest' })
    }, [activeIndex])

    if (!open) return null

    let running = -1

    return (
        <div className='fixed inset-0 z-[80] flex flex-col' role='dialog' aria-modal='true' aria-label={s.title}>
            <button
                type='button'
                aria-label={s.close}
                onClick={onClose}
                className='absolute inset-0 cursor-default bg-ink/60 backdrop-blur-sm'
            />

            <div className='relative mx-auto mt-3 flex max-h-[calc(100dvh-1.5rem)] w-[calc(100%-1.5rem)] max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-parchment shadow-[0_40px_100px_-30px_rgba(10,14,30,0.8)] sm:mt-[10vh] sm:max-h-[75vh]'>
                <div className='flex items-center gap-3 border-b border-border px-4 sm:px-5'>
                    <Search className='size-5 shrink-0 text-gold-deep' />
                    <input
                        ref={inputRef}
                        type='search'
                        value={query}
                        onChange={(event) => setQuery(event.target.value)}
                        onKeyDown={onInputKey}
                        placeholder={s.placeholder}
                        aria-label={s.title}
                        aria-controls='search-results'
                        autoComplete='off'
                        enterKeyHint='search'
                        className='h-16 min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground sm:text-lg [&::-webkit-search-cancel-button]:hidden'
                    />
                    {loading && <Loader2 className='size-5 shrink-0 animate-spin text-muted-foreground' aria-label={s.loading} />}
                    <button
                        type='button'
                        onClick={onClose}
                        aria-label={s.close}
                        className='grid size-9 shrink-0 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-navy/[0.06] hover:text-navy'
                    >
                        <X className='size-5' />
                    </button>
                </div>

                <div
                    ref={listRef}
                    id='search-results'
                    data-lenis-prevent=''
                    className='min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 sm:p-3'
                >
                    {term.length < 2 ? (
                        <p className='px-3 py-8 text-center text-sm text-muted-foreground'>{s.hint}</p>
                    ) : failed ? (
                        <p className='px-3 py-8 text-center text-sm text-muted-foreground'>{s.error}</p>
                    ) : !loading && total === 0 ? (
                        <p className='px-3 py-8 text-center text-sm text-muted-foreground'>{fill(s.empty, { query: term })}</p>
                    ) : (
                        GROUPS.filter((group) => results[group.key].length > 0).map((group) => (
                            <div key={group.key} className='mb-2 last:mb-0'>
                                <p className='flex items-center gap-2 px-3 pb-1.5 pt-3 text-[0.7rem] font-semibold uppercase tracking-[0.18em] text-gold-deep'>
                                    <group.icon className='size-3.5' />
                                    {s.groups[group.key]}
                                </p>
                                <ul>
                                    {results[group.key].map((result) => {
                                        running += 1
                                        const index = running
                                        const active = index === activeIndex
                                        return (
                                            <li key={result.href}>
                                                <a
                                                    href={localePath(result.href, locale)}
                                                    data-index={index}
                                                    onMouseEnter={() => setActiveIndex(index)}
                                                    onClick={(event) => {
                                                        event.preventDefault()
                                                        go(result)
                                                    }}
                                                    className={cn(
                                                        'flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors',
                                                        active ? 'bg-navy/[0.07]' : 'hover:bg-navy/[0.04]'
                                                    )}
                                                >
                                                    <span
                                                        className={cn(
                                                            'block shrink-0 overflow-hidden rounded-md bg-navy/10 ring-1 ring-border',
                                                            group.key === 'videos' ? 'aspect-video w-16' : 'h-12 w-9'
                                                        )}
                                                    >
                                                        {result.image ? (
                                                            <img src={result.image} alt='' loading='lazy' className='size-full object-cover' />
                                                        ) : (
                                                            <span className='grid size-full place-items-center text-navy/60'>
                                                                <group.icon className='size-4' />
                                                            </span>
                                                        )}
                                                    </span>
                                                    <span className='min-w-0 flex-1'>
                                                        <span lang={result.lang} dir={result.dir} className='block truncate text-sm font-semibold text-foreground'>
                                                            {result.title}
                                                        </span>
                                                        {result.subtitle && (
                                                            <span lang={result.lang} dir={result.dir} className='block truncate text-xs text-muted-foreground'>
                                                                {result.subtitle}
                                                            </span>
                                                        )}
                                                    </span>
                                                </a>
                                            </li>
                                        )
                                    })}
                                </ul>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    )
}
